<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import TramiteRow from './TramiteRow.vue';
import CreateTramiteModal from './CreateTramiteModal.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const query = ref('');
const statusFilter = ref('');
const createModalRef = ref(null);

const tramites = computed(() => store.getters['tramites/getTramites']);
const uiFlags = computed(() => store.getters['tramites/getUIFlags']);
const meta = computed(() => store.getters['tramites/getMeta']);

const STATUS_FILTERS = [
  '',
  'draft',
  'submitted',
  'in_review',
  'awaiting_citizen',
  'approved',
  'rejected',
  'completed',
  'cancelled',
];

const statusFilterLabel = key =>
  key
    ? t(`TRAMITES.STATUS.${key.toUpperCase()}`)
    : t('TRAMITES.FILTERS.ALL');

const fetch = () => {
  const params = {};
  if (statusFilter.value) params.by_status = statusFilter.value;
  if (query.value.trim()) params.q = query.value.trim();
  store.dispatch('tramites/fetchTramites', params);
};

const onSearch = () => fetch();

const applyStatus = key => {
  statusFilter.value = key;
  fetch();
};

const openCreate = () => createModalRef.value?.open();

const goToTramite = tramite => {
  router.push({
    name: 'tramite_details',
    params: { tramiteId: tramite.id },
  });
};

const onCreated = tramite => {
  if (tramite?.id) goToTramite(tramite);
};

const totalCount = computed(() => meta.value?.count || tramites.value.length);

onMounted(() => {
  fetch();
  store.dispatch('agents/get');
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Toolbar -->
    <header
      class="flex flex-col gap-3 px-6 pt-5 pb-3 border-b border-n-weak shrink-0"
    >
      <div class="flex items-center justify-between gap-3">
        <h1 class="flex items-center gap-2 text-xl font-semibold text-n-slate-12">
          <span class="i-lucide-stamp text-n-slate-10" />
          {{ t('TRAMITES.HEADER_TITLE') }}
          <span class="text-base font-normal text-n-slate-10">
            {{ totalCount }}
          </span>
        </h1>
        <div class="flex items-center gap-2">
          <div class="relative w-52 max-w-full">
            <span
              class="absolute -translate-y-1/2 i-lucide-search left-2.5 top-1/2 text-n-slate-10"
            />
            <input
              v-model="query"
              type="text"
              :placeholder="t('TRAMITES.SEARCH_PLACEHOLDER')"
              class="w-full h-8 pl-8 pr-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
              @keyup.enter="onSearch"
            />
          </div>
          <Button
            :label="t('TRAMITES.ACTIONS.NEW')"
            icon="i-lucide-plus"
            size="sm"
            color="blue"
            @click="openCreate"
          />
        </div>
      </div>

      <!-- Status filters -->
      <nav class="flex flex-wrap items-center gap-1.5">
        <button
          v-for="key in STATUS_FILTERS"
          :key="key || 'all'"
          class="px-2.5 h-7 text-sm rounded-lg transition-colors"
          :class="
            statusFilter === key
              ? 'bg-n-brand/10 text-n-blue-text font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="applyStatus(key)"
        >
          {{ statusFilterLabel(key) }}
        </button>
      </nav>
    </header>

    <p
      v-if="!tramites.length && !uiFlags.isFetching"
      class="p-12 text-sm italic text-center text-n-slate-10"
    >
      {{ query || statusFilter ? t('TRAMITES.NO_RESULTS') : t('TRAMITES.EMPTY') }}
    </p>

    <!-- List -->
    <div v-else class="flex-1 overflow-y-auto divide-y divide-n-weak/60">
      <TramiteRow
        v-for="tramite in tramites"
        :key="tramite.id"
        :tramite="tramite"
        @open="goToTramite"
      />
    </div>

    <CreateTramiteModal ref="createModalRef" @created="onCreated" />
  </div>
</template>
